import { useQuery } from '@apollo/client/react';
import { Button, InputAdornment, Stack, TextField, Typography, Grid } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import { ProjectContext } from '../../../Contexts/Project/Context';

import { isValidTitle, MIN_TITLE_LENGTH } from './validation';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { GetProjectCodeSuggestionDocument } from '@/graphql/generated/graphql';
import TextEditor from '@/TextEditor';
import { getSubProjectCodeInitials } from '@/utils/helperFunctions';

const SUGGESTION_DEBOUNCE_MS = 500;

const ProjectBasics: React.FC = () => {
  const { currentUser } = useContext(CurrentUserContext);
  const { mode, project, parentProject, onProjectDataChange, isProjectCodeUnique } = useContext(ProjectContext);

  // Debounce the title so the suggestion query doesn't fire on every keystroke
  const [debouncedTitle, setDebouncedTitle] = useState(project.title);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTitle(project.title), SUGGESTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [project.title]);

  const { data: codeSuggestionQuery } = useQuery(GetProjectCodeSuggestionDocument, {
    fetchPolicy: 'network-only',
    skip: !currentUser?.defaultCompany?.id || !debouncedTitle || mode === 'edit',
    variables: { title: debouncedTitle as string, parentCode: parentProject?.code as string },
  });

  const suggestedCode = codeSuggestionQuery?.getProjectCodeSuggestion;
  const showSuggestion = Boolean(project.title && suggestedCode && project.code !== suggestedCode);

  return (
    <Grid container mb={2}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          autoComplete="off"
          name="project-title"
          required
          fullWidth
          value={project.title}
          id="project-title"
          label="Project Title"
          autoFocus
          onChange={(e) => onProjectDataChange('title', e.target.value)}
          error={!isValidTitle(project.title)}
          helperText={!isValidTitle(project.title) ? `Project title minimum ${MIN_TITLE_LENGTH} characters` : ''}
          slotProps={{ input: { sx: { backgroundColor: 'background.paper' } } }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          required
          fullWidth
          id="project-code"
          error={!isProjectCodeUnique(project.code)}
          helperText={!isProjectCodeUnique(project.code) ? 'Project code must be unique' : ''}
          value={project.code}
          label="Project Code"
          onChange={(e) => onProjectDataChange('code', e.target.value)}
          disabled={mode === 'edit'}
          slotProps={{
            input: {
              sx: { backgroundColor: 'background.paper' },
              ...(parentProject
                ? {
                    startAdornment: (
                      <InputAdornment position="start">{getSubProjectCodeInitials(parentProject?.code)}</InputAdornment>
                    ),
                  }
                : {}),
            },
          }}
        />
        {mode === 'create' && showSuggestion && (
          <Stack direction="row" sx={{ alignItems: 'center', marginTop: 0.5 }}>
            <Typography variant="caption">Suggested:</Typography>
            <Button size="small" onClick={() => onProjectDataChange('code', suggestedCode as string)}>
              {getSubProjectCodeInitials(parentProject?.code)}
              {suggestedCode}
            </Button>
          </Stack>
        )}
      </Grid>
      <TextEditor
        setText={(val: string) => onProjectDataChange('description', val)}
        saveOn="update"
        text={project.description ?? ''}
        minRows={5}
      />
    </Grid>
  );
};

export default ProjectBasics;
